import { useCurrentUser } from '@/hooks/useCurrentUser';
import { storage } from './storage';

const API_BASE = (import.meta.env.VITE_BASE_URL as string) || 'https://mlb.meralunchbox.in/api';

let watchId: number | null = null;
let intervalId: ReturnType<typeof setInterval> | null = null;
let activeUsername = '';
let lastLat: number | null = null;
let lastLng: number | null = null;
let lastLocTime = 0;

// ─── Service Worker (background fallback) ────────────────────────────────────

function postToSW(msg: object) {
  navigator.serviceWorker?.controller?.postMessage(msg);
}

async function registerSW() {
  if (!('serviceWorker' in navigator)) return;
  try {
    const reg = await navigator.serviceWorker.register('/sw-location.js');
    // If SW just installed, activate it immediately via skipWaiting + clients.claim
    if (reg.installing || reg.waiting) {
      await new Promise<void>((resolve) => {
        const sw = reg.installing ?? reg.waiting!;
        sw.addEventListener('statechange', function handler() {
          if (sw.state === 'activated') { sw.removeEventListener('statechange', handler); resolve(); }
        });
      });
    }
    // Wait until this page is controlled
    if (!navigator.serviceWorker.controller) {
      await new Promise<void>((resolve) => {
        navigator.serviceWorker.addEventListener('controllerchange', () => resolve(), { once: true });
      });
    }
  } catch (_) { }
}

// ─── Direct POST (used by main-thread interval) ───────────────────────────────

async function postLocation() {
  if (!activeUsername || lastLat === null || lastLng === null) return;
  if (Date.now() - lastLocTime > 30000) return;

  try {
    const res = await fetch(`${API_BASE}/update-location/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${storage.getAuthToken() || ''}`,
      },
      body: JSON.stringify({
        username: activeUsername,
        latitude: String(lastLat),
        longitude: String(lastLng),
      }),
    });

    // 🔥 HANDLE 401 HERE
    if (res.status === 401) {
      handleUnauthorized();
    }

  } catch (_) { }
}

function handleUnauthorized() {

  // stop tracking
  stopLocationTracking();

  // clear tokens
  storage.clearAuthToken();
  storage.setRefreshToken('');

  // optional: clear everything
  storage.logout?.();

  // 🔥 redirect to login
  window.location.href = "/#/login";
}

// ─── Public API ───────────────────────────────────────────────────────────────

export async function startLocationTracking(userDetails: any) {
  const role = (userDetails?.role as string | undefined) ?? '';
  if (role !== "Delivery Boy") return;
  if (watchId !== null) return;
  activeUsername = userDetails?.username || '';
  if (!navigator.geolocation) return;

  // ✅ STEP 1: Get current location immediately
  navigator.geolocation.getCurrentPosition(
    (pos) => {
      lastLat = pos.coords.latitude;
      lastLng = pos.coords.longitude;
      lastLocTime = Date.now();

      // 🔥 Immediately send correct location
      postLocation();

      // Sync with service worker
      postToSW({
        type: 'UPDATE_LOCATION',
        latitude: lastLat,
        longitude: lastLng,
        token: storage.getAuthToken() || '',
      });
    },
    (err) => {
      console.log("Initial location error", err);
    },
    { enableHighAccuracy: true }
  );

  // ✅ STEP 2: Then start watching
  watchId = navigator.geolocation.watchPosition(
    (pos) => {
      lastLat = pos.coords.latitude;
      lastLng = pos.coords.longitude;
      lastLocTime = Date.now();

      postToSW({
        type: 'UPDATE_LOCATION',
        latitude: lastLat,
        longitude: lastLng,
        token: storage.getAuthToken() || '',
      });
    },
    null,
    { enableHighAccuracy: true, maximumAge: 0, timeout: 10000 }
  );

  intervalId = setInterval(postLocation, 2000);
}

export function stopLocationTracking() {
  if (watchId !== null) {
    navigator.geolocation?.clearWatch(watchId);
    watchId = null;
  }
  if (intervalId !== null) {
    clearInterval(intervalId);
    intervalId = null;
  }
  postToSW({ type: 'STOP_TRACKING' });
  activeUsername = '';
  lastLat = null;
  lastLng = null;
}

export function isLocationTracking() {
  return watchId !== null;
}
